import type { AxiosResponse } from "axios";
import axios, { isAxiosError } from "axios";
import {
  hasWindow,
  logger as parentLogger,
  sentryBreadcrumb,
} from "@litres/utils";

interface ILoggingInterceptorParams {
  response: AxiosResponse;
}

const ENABLE_AXIOS_LOGS = true;

const logger = parentLogger.child({
  context: "axios",
});

const createAxiosInstance = () => {
  if (typeof window === "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
    const http = require("http");
    // eslint-disable-next-line @typescript-eslint/no-require-imports,@typescript-eslint/no-var-requires
    const https = require("https");
    return axios.create({
      httpAgent: new http.Agent({ keepAlive: true, scheduling: "fifo" }),
      httpsAgent: new https.Agent({ keepAlive: true, scheduling: "fifo" }),
    });
  }
  return axios.create({});
};

export const axiosInstance = createAxiosInstance();

function loggingInterceptor({ response }: ILoggingInterceptorParams) {
  if (process.env.NODE_ENV === "development") {
    return;
  }

  const logData = {
    status: Number(response.status),
    method: response.config.method || "",
    url: response.config.url || "",
    responseHeaders: { ...response.headers },
    requestHeaders: { ...response.config.headers },
    requestData: response.config.params,
  };

  if (hasWindow) {
    // sentry breadcrumb для клиентского лога запроса
    sentryBreadcrumb({
      category: "api",
      level: "info",
      message: `${response.config.method || ""} ${response.config.url || ""}`,
      data: logData,
    });
  }

  // общий логгер только для SSR (graylog)
  if (!hasWindow && ENABLE_AXIOS_LOGS) {
    logger.info(logData);
  }
}

axiosInstance.interceptors.response.use(
  response => {
    loggingInterceptor({ response });
    return response;
  },
  error => {
    // TODO: поискать в issues axios, почему они error типизируют как any
    // хотя возвращается AxiosError -> как результат можно законтрибьютить в axios
    if (isAxiosError(error) && error.response) {
      loggingInterceptor({ response: error.response });
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
